import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CheckCircle, Clock, User, DollarSign } from 'lucide-react'
import type { VencimientoConCliente } from '@/types/vencimientos'
import type { CatalogoImpuesto } from '@/types/impuestos'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import { format, parseISO, isToday, isTomorrow, isPast, startOfDay } from 'date-fns'
import { formatearDiaCalendario } from '@/lib/diaCalendario'

interface VencimientoCardProps {
  vencimiento: VencimientoConCliente
  onMarcarPresentado: (vencimiento: VencimientoConCliente) => void
  onVerDetalles?: (vencimiento: VencimientoConCliente) => void
  onRegistrarPago?: (vencimiento: VencimientoConCliente) => void
  compact?: boolean
  catalogo?: CatalogoImpuesto[]
}

export function VencimientoCard({
  vencimiento,
  onMarcarPresentado,
  onVerDetalles,
  onRegistrarPago,
  compact = false,
  catalogo = [],
}: VencimientoCardProps) {
  const fechaVenc = parseISO(vencimiento.fecha_vencimiento ?? '')
  const esHoy = isToday(fechaVenc)
  const esManana = isTomorrow(fechaVenc)
  // Vencido = día estrictamente anterior a hoy (igual que la vista SQL: fecha < CURRENT_DATE).
  // Normalizar a startOfDay + excluir hoy evita marcar como vencido lo que vence HOY.
  const estaVencido = isPast(startOfDay(fechaVenc)) && !esHoy && vencimiento.estado === 'pendiente'
  const extended = vencimiento as unknown as { monto_determinado?: number | null }
  const tieneMonto = extended.monto_determinado != null && extended.monto_determinado > 0
  const diaPresentado = formatearDiaCalendario(vencimiento.fecha_presentacion)

  const getTipoColor = (tipo: string | null) => getImpuestoColorClasses(tipo ?? '', catalogo)

  if (compact) {
    return (
      <div className={`flex items-center justify-between p-2 rounded-lg border ${
        estaVencido ? 'border-red-200 bg-red-50' :
        esHoy ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={getTipoColor(vencimiento.tipo_impuesto)}>
            {vencimiento.tipo_impuesto}
          </Badge>
          <span className="text-sm truncate">{vencimiento.cliente_nombre}</span>
        </div>
        {vencimiento.estado === 'pendiente' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-green-600"
            onClick={() => onMarcarPresentado(vencimiento)}
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
        )}
        {vencimiento.estado === 'presentado' && (
          <CheckCircle className="h-4 w-4 text-green-600" />
        )}
        {vencimiento.estado === 'pagado' && (
          <DollarSign className="h-4 w-4 text-blue-600" />
        )}
        {vencimiento.estado === 'plan_pago' && (
          <Badge variant="outline" className="text-violet-700 bg-violet-100 border-0 text-xs">Plan</Badge>
        )}
      </div>
    )
  }

  const getEstadoBadgeFiltered = () => {
    if (vencimiento.estado === 'pagado') {
      return <Badge variant="default" className="bg-blue-600 text-xs">Pagado</Badge>
    }
    if (vencimiento.estado === 'plan_pago') {
      return <Badge variant="default" className="bg-violet-600 text-xs">Plan de Pago</Badge>
    }
    if (vencimiento.estado === 'presentado') {
      return <Badge variant="default" className="bg-green-600 text-xs">Presentado</Badge>
    }
    if (vencimiento.estado === 'vencido' || estaVencido) {
      return <Badge variant="destructive" className="text-xs">Vencido</Badge>
    }
    if (esHoy) {
      return <Badge variant="default" className="bg-orange-500 text-xs">Hoy</Badge>
    }
    if (esManana) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">Mañana</Badge>
    }
    return null
  }

  return (
    <Card className={`transition-colors ${
      estaVencido ? 'border-red-300 bg-red-950/30 dark:bg-red-950/30 bg-red-50/80' :
      esHoy ? 'border-orange-200 bg-orange-50/50' : ''
    }`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-0.5 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge variant="outline" className={`${getTipoColor(vencimiento.tipo_impuesto)} text-xs`}>
                {vencimiento.tipo_impuesto}
              </Badge>
              {getEstadoBadgeFiltered()}
            </div>

            <h4 className="font-medium text-sm">{vencimiento.cliente_nombre}</h4>

            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
              <Clock className="h-3 w-3 shrink-0" />
              <span>{format(fechaVenc, 'dd/MM')}</span>
              {vencimiento.periodo && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <span>Per: {vencimiento.periodo}</span>
                </>
              )}
              {vencimiento.cliente_responsable && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <User className="h-3 w-3 shrink-0" />
                  <span>{vencimiento.cliente_responsable}</span>
                </>
              )}
            </div>

            {vencimiento.estado === 'presentado' && diaPresentado && (
              <p className="text-xs text-green-600">
                Presentado el {diaPresentado}
                {vencimiento.numero_presentacion && ` - N° ${vencimiento.numero_presentacion}`}
              </p>
            )}

            {vencimiento.observaciones && (
              <p className="text-xs text-muted-foreground italic truncate" title={vencimiento.observaciones}>
                {vencimiento.observaciones}
              </p>
            )}
          </div>

          {/* Actions based on state */}
          <div className="flex flex-col gap-1 shrink-0">
            {(vencimiento.estado === 'pendiente' || vencimiento.estado === 'vencido' || estaVencido) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
                onClick={() => onMarcarPresentado(vencimiento)}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                <span className="text-xs">Presentar</span>
              </Button>
            )}
            {vencimiento.estado === 'presentado' && tieneMonto && onRegistrarPago && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                onClick={() => onRegistrarPago(vencimiento)}
              >
                <DollarSign className="h-4 w-4 mr-1" />
                <span className="text-xs">Pagar</span>
              </Button>
            )}
            {(vencimiento.estado === 'presentado' || vencimiento.estado === 'pagado' || vencimiento.estado === 'plan_pago') && onVerDetalles && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground hover:bg-muted"
                onClick={() => onVerDetalles(vencimiento)}
              >
                <span className="text-xs">Detalles</span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
